import { useEffect, useMemo, useState } from 'react';
import {
  FiCalendar,
  FiClock,
  FiEdit2,
  FiFilter,
  FiHash,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiUpload,
  FiDownload,
  FiCopy,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import Api from '../../Api';
import {
  Button,
  Card,
  Input,
  Modal,
  PageComponent,
  Skeletons,
  Select,
  MultiSelect,
  Table,
  JourneyForm,
  JourneyImportModal,
  TableActions,
} from '../../components';
import { usePermission } from '../../hooks/usePermission';
import '../CompanyManager/style.css';

export default function JourneyManager() {
  const permissions = usePermission('time_keeping');
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedJourney, setSelectedJourney] = useState(null);
  const [isCloneMode, setIsCloneMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [journeyToDelete, setJourneyToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [company, setCompany] = useState(null);
  
  // New state for multi-company support
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('all');
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    async function loadInitialData() {
      try {
        const { data: companiesData } = await Api.get('/companies');
        setCompanies(companiesData || []);

        const storedCompany = localStorage.getItem('selectedCompany');
        if (storedCompany) {
          const parsed = JSON.parse(storedCompany);
          if (parsed && parsed.id) {
            setCompany(parsed);
            // Default to "all" as requested, or keep stored company?
            // User requested "all" as default in previous task, let's stick to that pattern if desired, 
            // but usually we respect the stored company if it's the first load. 
            // However, the prompt says "deixar por padrão a 'TODAS' filtrado no select".
            setCompanyFilter('all'); 
            loadJourneys('all', companiesData || []);
            return;
          }
        }
        
        setCompanyFilter('all');
        loadJourneys('all', companiesData || []);
      } catch (error) {
        console.error('Error loading initial data', error);
        // Fallback
        setCompanyFilter('all');
        loadJourneys('all', []);
      }
    }

    loadInitialData();
  }, [navigate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, companyFilter]);

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyFilterValues;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map(c => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      const allIds = companies.map(c => String(c.id));
      if (specificIds.length === allIds.length && allIds.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    setCompanyFilterValues(nextValues);

    const idsWithoutAll = nextValues.filter(v => v !== allValue);
    if (idsWithoutAll.length === 1) {
      const id = idsWithoutAll[0];
      setCompanyFilter(id);
    } else {
      setCompanyFilter('all');
    }

    loadJourneys('all', companies, nextValues);
  }

  async function loadJourneys(filterValue = companyFilter, companiesList = companies, selectedValues = null) {
    try {
      setLoading(true);

      const allValue = 'all';
      let idsToLoad = [];

      if (selectedValues && Array.isArray(selectedValues)) {
        const hasAllOverride = selectedValues.includes(allValue);
        const explicit = selectedValues.filter(v => v && v !== allValue);
        if (hasAllOverride || explicit.length === 0) {
          idsToLoad = companiesList.map(c => c.id);
        } else {
          idsToLoad = explicit.map(id => Number(id));
        }
      } else if (filterValue === 'all') {
        const hasAll = companyFilterValues.includes(allValue);
        const explicit = companyFilterValues.filter(v => v && v !== allValue);
        if (hasAll || explicit.length === 0) {
          idsToLoad = companiesList.map(c => c.id);
        } else {
          idsToLoad = explicit.map(id => Number(id));
        }
      } else {
        idsToLoad = [Number(filterValue)];
      }

      const uniqueIds = Array.from(new Set(idsToLoad)).filter(Boolean);

      if (uniqueIds.length === 0) {
        setJourneys([]);
        setError('');
        return;
      }

      let data = [];

      if (uniqueIds.length === 1) {
        const companyId = uniqueIds[0];
        const selectedComp = companiesList.find(c => c.id === companyId);
        let res = await Api.getJourneys(companyId);
        if (Array.isArray(res)) {
          data = res.map(j => ({ ...j, company: selectedComp }));
        } else {
          data = [];
        }
      } else {
        const results = await Promise.all(
          uniqueIds.map(id => {
            const comp = companiesList.find(c => c.id === id);
            return Api.getJourneys(id)
              .then(res =>
                Array.isArray(res)
                  ? res.map(j => ({ ...j, company: comp }))
                  : []
              )
              .catch(() => []);
          })
        );
        data = results.flat();
      }

      if (Array.isArray(data)) {
        // Sort by name
        data.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setJourneys(data);
        setError('');
      } else {
        setJourneys([]);
        setError('Não foi possível carregar as jornadas.');
      }

      setHasLoadedOnce(true);
    } catch (err) {
      console.error(err);
      setError('Erro ao carregar jornadas. Tente novamente.');
      setJourneys([]);
    } finally {
      setLoading(false);
    }
  }

  function handleCreate() {
    setSelectedJourney(null);
    setIsCloneMode(false);
    setIsModalOpen(true);
  }

  function handleEdit(journey) {
    setSelectedJourney(journey);
    setIsCloneMode(false);
    setIsModalOpen(true);
  }

  function handleFormSuccess() {
    loadJourneys();
    setIsModalOpen(false);
    setSelectedJourney(null);
    setIsCloneMode(false);
  }

  function handleRequestDelete(journey) {
    setJourneyToDelete(journey);
    setIsDeleteModalOpen(true);
  }

  async function handleConfirmDelete() {
    if (!journeyToDelete) return;

    try {
      setIsDeleting(true);
      const targetCompanyId = journeyToDelete.company?.id || (companyFilter !== 'all' ? companyFilter : null) || journeyToDelete.company_id;
      
      if (!targetCompanyId) {
        alert('Erro: Empresa não identificada para exclusão.');
        return;
      }

      const result = await Api.deleteJourney(targetCompanyId, journeyToDelete.id);

      if (result && !result.error) {
        setJourneys(prev => prev.filter(j => j.id !== journeyToDelete.id));
        setIsDeleteModalOpen(false);
        setJourneyToDelete(null);
      } else {
        alert(
          result && result.error
            ? result.error
            : 'Erro ao excluir jornada.',
        );
      }
    } catch (err) {
      alert('Erro ao excluir jornada.');
    } finally {
      setIsDeleting(false);
    }
  }

  function handleCancelDelete() {
    if (isDeleting) return;

    setIsDeleteModalOpen(false);
    setJourneyToDelete(null);
  }

  function handleImport() {
    setIsImportModalOpen(true);
  }

  function handleExport() {
    if (!journeys || journeys.length === 0) {
      toast.warning('Não há dados para exportar.');
      return;
    }

    const exportData = journeys.map(j => {
      const baseData = {
        'Empresa': j.company ? (j.company.corporate_name || j.company.trade_name || `Empresa ${j.company.id}`) : 'N/A',
        'Nome': j.name,
        'Tipo': j.type || 'Padrão',
        'Descrição': j.description || '',
        'Carga Semanal': j.weekly_hours || '',
        'Carga Diária': j.daily_hours || '',
        'Entrada': j.start_time || '',
        'Saída': j.end_time || '',
        'Início Intervalo': j.break_start_time || '',
        'Fim Intervalo': j.break_end_time || '',
        'Duração Intervalo': j.break_duration || '',
      };

      // Add detailed work days if available
      const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      const dayLabels = {
        monday: 'Segunda',
        tuesday: 'Terça',
        wednesday: 'Quarta',
        thursday: 'Quinta',
        friday: 'Sexta',
        saturday: 'Sábado',
        sunday: 'Domingo'
      };

      days.forEach(day => {
        const dayConfig = j.work_days ? j.work_days[day] : null;
        const label = dayLabels[day];
        
        baseData[`${label} Entrada`] = dayConfig && dayConfig.enabled ? dayConfig.start || '' : '';
        baseData[`${label} Saída`] = dayConfig && dayConfig.enabled ? dayConfig.end || '' : '';
        baseData[`${label} Início Intervalo`] = dayConfig && dayConfig.enabled ? dayConfig.breakStart || '' : '';
        baseData[`${label} Fim Intervalo`] = dayConfig && dayConfig.enabled ? dayConfig.breakEnd || '' : '';
      });

      return baseData;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Jornadas");
    XLSX.writeFile(wb, "jornadas.xlsx");
  }

  function handleClone(journey) {
    const targetCompanyId = journey.company?.id || journey.company_id || (companyFilter !== 'all' ? Number(companyFilter) : null);
    const cloneData = {
      name: `${journey.name} (cópia)`,
      type: journey.type,
      description: journey.description || null,
      weekly_hours: journey.weekly_hours ?? null,
      daily_hours: journey.daily_hours ?? null,
      work_days: journey.work_days || null,
      rotation_details: journey.rotation_details || null,
      start_time: journey.start_time || null,
      end_time: journey.end_time || null,
      break_start_time: journey.break_start_time || null,
      break_end_time: journey.break_end_time || null,
      company_id: targetCompanyId || null,
    };
    setSelectedJourney(cloneData);
    setIsCloneMode(true);
    setIsModalOpen(true);
  }

  const filteredJourneys = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return journeys.filter(journey => {
      const matchesSearch =
        !term ||
        (journey.name || '').toLowerCase().includes(term) ||
        (journey.description || '').toLowerCase().includes(term) ||
        (journey.weekly_hours != null
          ? String(journey.weekly_hours).toLowerCase()
          : ''
        ).includes(term) ||
        (journey.daily_hours != null
          ? String(journey.daily_hours).toLowerCase()
          : ''
        ).includes(term);

      return matchesSearch;
    });
  }, [journeys, searchTerm]);

  const totalItems = filteredJourneys.length;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedJourneys = filteredJourneys.slice(
    startIndex,
    startIndex + pageSize,
  );

  const parseTime = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  function formatBreakDuration(value) {
    if (value === undefined || value === null || String(value).trim() === '') {
      return 'Não informado';
    }

    const num = Number(value);

    if (!Number.isNaN(num) && num > 0) {
      const hours = Math.floor(num / 60);
      const minutes = num % 60;

      if (hours > 0 && minutes > 0) {
        return `${hours}h ${minutes}min`;
      }

      if (hours > 0) {
        return `${hours}h`;
      }

      return `${minutes}min`;
    }

    return String(value);
  }

  const columns = [
    {
      id: 'journey',
      header: 'Jornada',
      width: '2.2fr',
      cellClassName: 'manager-table-cell-primary',
      render: journey => (
        <>
          <div className="manager-table-avatar">
            <FiClock size={18} />
          </div>
          <div className="manager-table-cell-text">
            <span className="manager-table-title">
              {journey.name}
            </span>
            <span className="manager-table-subtitle">
              ID: {journey.id} • {journey.type || 'Padrão'}
            </span>
          </div>
        </>
      ),
    },
    {
      id: 'hours',
      header: 'Carga horária',
      width: '2fr',
      cellClassName: 'manager-table-cell',
      render: journey => {
        const weekly =
          journey.weekly_hours != null
            ? `${journey.weekly_hours} h/semana`
            : 'Semanal não informada';
        const daily =
          journey.daily_hours != null
            ? `${journey.daily_hours} h/dia`
            : 'Diária não informada';

        return (
          <div className="manager-table-cell-text">
            <span className="manager-table-subtitle">
              {weekly}
            </span>
            <span className="manager-table-subtitle">
              {daily}
            </span>
          </div>
        );
      },
    },
    {
      id: 'schedule',
      header: 'Horário',
      width: '1.6fr',
      cellClassName: 'manager-table-cell manager-table-col-cnpj',
      render: journey => {
        if (journey.rotation_details && journey.rotation_details.shifts && journey.rotation_details.shifts.length > 0) {
            const freq = journey.rotation_details.frequency === 'weekly' ? 'Semanal' :
                         journey.rotation_details.frequency === 'daily' ? 'Diária' : 'Personalizada';
            return (
                <div className="manager-table-cnpj">
                    <FiCalendar size={14} />
                    <span>Rotação {freq}</span>
                </div>
            );
        }

        if (journey.work_days && Object.values(journey.work_days).some(d => d.enabled)) {
          const daysCount = Object.values(journey.work_days).filter(d => d.enabled).length;
          return (
            <div className="manager-table-cnpj">
              <FiCalendar size={14} />
              <span>{daysCount} dias config.</span>
            </div>
          );
        }

        const start = journey.start_time ? journey.start_time.substring(0, 5) : '--:--';
        const end = journey.end_time ? journey.end_time.substring(0, 5) : '--:--';

        return (
          <div className="manager-table-cnpj">
            <FiHash size={14} />
            <span>
              {start} - {end}
            </span>
          </div>
        );
      },
    },
    {
      id: 'break',
      header: 'Intervalo',
      width: '1.4fr',
      cellClassName: 'manager-table-cell',
      render: journey => {
        let displayValue = null;

        // 1. Fixed Schedule Times (Root level)
        if (journey.break_start_time && journey.break_end_time) {
          const start = journey.break_start_time.substring(0, 5);
          const end = journey.break_end_time.substring(0, 5);
          displayValue = `${start} - ${end}`;
        }
        // 2. Weekly Schedule (Check for consistent times)
        else if (journey.work_days) {
          let lastStart = null;
          let lastEnd = null;
          let isVariable = false;
          let hasAnyBreak = false;

          const days = Object.values(journey.work_days).filter(d => d.enabled);
          if (days.length > 0) {
            for (const d of days) {
              if (d.breakStart && d.breakEnd) {
                // Found a break
                if (hasAnyBreak) {
                  // Check consistency with previous
                  if (lastStart !== d.breakStart || lastEnd !== d.breakEnd) {
                    isVariable = true;
                    break;
                  }
                }
                hasAnyBreak = true;
                lastStart = d.breakStart;
                lastEnd = d.breakEnd;
              } else if (hasAnyBreak) {
                // If we found a break before, but this enabled day has none, it's variable/inconsistent
                // Unless we treat "no break" as distinct. 
                // Let's assume if some days have breaks and others don't, it's mixed.
                // But for now, let's just track the times if they exist.
              }
            }

            if (isVariable) {
              displayValue = 'Variável';
            } else if (hasAnyBreak && lastStart && lastEnd) {
               // Ensure substring if they are HH:mm:ss
               const s = lastStart.length > 5 ? lastStart.substring(0, 5) : lastStart;
               const e = lastEnd.length > 5 ? lastEnd.substring(0, 5) : lastEnd;
               displayValue = `${s} - ${e}`;
            }
          }
        }

        return (
          <span className="manager-table-subtitle">
            {displayValue || 'Não informado'}
          </span>
        );
      },
    },
    {
      id: 'company',
      header: 'Empresa',
      width: '1.5fr',
      cellClassName: 'manager-table-cell',
      render: journey => {
        const companyName = journey.company ? (journey.company.corporate_name || journey.company.trade_name) : 'N/A';
        return <span className="manager-table-text">{companyName}</span>;
      },
    },
    {
      id: 'actions',
      header: 'Ações',
      width: '0.6fr',
      cellClassName: 'manager-table-cell manager-table-col-actions',
      render: journey => (
        <TableActions>
          {permissions.canEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(journey);
              }}
            >
              <FiEdit2 size={16} />
              Editar
            </button>
          )}
          {permissions.canCreate && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClone(journey);
              }}
            >
              <FiCopy size={16} />
              Clonar
            </button>
          )}
          {permissions.canDelete && (
            <button
              type="button"
              className="action-danger"
              onClick={(e) => {
                e.stopPropagation();
                handleRequestDelete(journey);
              }}
            >
              <FiTrash2 size={16} />
              Excluir
            </button>
          )}
        </TableActions>
      ),
    },
  ];

  function JourneyManagerSkeletonWrapper() {
    const { CompanyManagerSkeleton } = Skeletons;
    return <CompanyManagerSkeleton />;
  }

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiClock size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar este módulo.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        {loading && !hasLoadedOnce && (
          <JourneyManagerSkeletonWrapper />
        )}

        {(hasLoadedOnce || !loading) && (
          <>
            <div className="manager-header">
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiClock size={22} />
                </div>
                <div>
                  <h1>Jornadas</h1>
                  <p>
                    Visualize, filtre e gerencie todas as jornadas da empresa selecionada.
                  </p>
                </div>
              </div>
              <div className="manager-header-actions" style={{ display: 'flex', gap: '8px' }}>
                {permissions.canCreate && (
                  <Button
                    onClick={handleImport}
                    className="manager-header-button"
                    variant="secondary"
                  >
                    <FiUpload size={18} />
                    Importar
                  </Button>
                )}
                <Button
                  onClick={handleExport}
                  className="manager-header-button"
                  variant="secondary"
                >
                  <FiDownload size={18} />
                  Exportar
                </Button>
                {permissions.canCreate && (
                  <Button onClick={handleCreate} className="manager-header-button">
                    <FiPlus size={18} />
                    Nova Jornada
                  </Button>
                )}
              </div>
            </div>

            <Card className="manager-filters-card">
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
                {journeys.length > 0 && (
                  <span className="manager-filters-count">
                    {filteredJourneys.length} de {journeys.length} jornadas
                  </span>
                )}
              </div>

              <div className="manager-filters-grid-custom">
                <div className="manager-filter-group">
                  <MultiSelect
                    id="company-filter"
                    label="Empresa"
                    value={companyFilterValues}
                    onChange={handleCompanyFilterChange}
                    options={[
                      { value: 'all', label: 'TODAS' },
                      ...companies.map(c => ({
                        value: String(c.id),
                        label: c.corporate_name || c.trade_name || `Empresa ${c.id}`,
                      })),
                    ]}
                    searchable
                  />
                </div>

                <div className="manager-filter-group">
                  <label className="manager-filter-label">Buscar</label>
                  <div className="manager-filter-search">
                    <Input
                      id="journey-search"
                      placeholder="Busque por nome, descrição ou horas"
                      value={searchTerm}
                      onChange={event => setSearchTerm(event.target.value)}
                      icon={FiSearch}
                    />
                  </div>
                </div>
              </div>
            </Card>

            {error && <div className="error">{error}</div>}

            {!loading && !error && journeys.length === 0 && (
              <div className="empty-state">
                <p>Nenhuma jornada cadastrada.</p>
              </div>
            )}

            {!loading && !error && journeys.length > 0 && (
              <Table
                items={paginatedJourneys}
                columns={columns}
                totalItems={totalItems}
                page={safePage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={value => {
                  setPageSize(value);
                  setCurrentPage(1);
                }}
                getRowKey={journey => journey.id}
                emptyMessage="Nenhuma jornada encontrada com os filtros atuais."
              />
            )}
          </>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedJourney ? 'Editar Jornada' : 'Nova Jornada'}
        closeOnOverlayClick={false}
      >
        <JourneyForm
          companyId={isCloneMode ? (selectedJourney?.company_id ?? (companyFilter !== 'all' ? Number(companyFilter) : null)) : (companyFilter !== 'all' ? Number(companyFilter) : null)}
          companies={companies}
          initialData={selectedJourney}
          onSuccess={handleFormSuccess}
          onCancel={() => setIsModalOpen(false)}
          forceCreate={isCloneMode}
        />
      </Modal>

      <JourneyImportModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          onSuccess={() => loadJourneys()}
          companyId={companyFilter !== 'all' ? Number(companyFilter) : null}
          companies={companies}
        />

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        title="Confirmar exclusão"
        closeOnOverlayClick={!isDeleting}
      >
        <div className="delete-modal-content">
          <p>
            Tem certeza que deseja excluir a jornada{' '}
            {journeyToDelete && (
              <span className="delete-modal-company-name">
                {journeyToDelete.name}
              </span>
            )}
            ?
          </p>
          <p>
            Essa ação não poderá ser desfeita.
          </p>
        </div>
        <div className="delete-modal-actions">
          <Button
            type="button"
            className="cancel-button"
            onClick={handleCancelDelete}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </div>
      </Modal>
    </PageComponent>
  );
}

